import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, User, PawPrint, Scan } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase, isSupabaseAuthConfigured } from '../services/mockData';
import { loadUserProfile } from '../services/authProfile';
import { SessionUser } from '../types';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSeo } from '../utils/useSeo';

interface LoginProps {
  onLogin: (user: SessionUser) => void;
}

/** .env ile kabul edilmeyen bariz zayıf şifreler (sunucu tarafı yapılandırması için) */
const BLOCKED_ENV_PASSWORDS = new Set([
  '123456',
  '12345678',
  '123456789',
  'password',
  'qwerty',
  '111111',
  '123123',
]);

type ClinicEnvResult =
  | { ok: true }
  | { ok: false; reason: 'missing_env' | 'weak_password' | 'mismatch' };

const isDev = process.env.NODE_ENV !== 'production';

/** Aynı hatada çift toast (React Strict Mode vb.) önlenir */
const loginToastId = 'login-flow-msg';

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  useSeo({ title: 'Giriş | KlinikVet', path: '/login', noindex: true });
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guestAutoStarted = useRef(false);

  const handleGuestLogin = useCallback(() => {
    localStorage.setItem('authToken', 'guest-token-' + Date.now());
    onLogin({
      name: 'Misafir',
      email: 'misafir@preview.local',
      role: 'Misafir',
      clinicId: null,
      clinicName: 'Önizleme',
      authProvider: 'guest',
    });
    toast.success('Misafir olarak açıldı — panel arayüzünü gezebilirsiniz.');
    navigate('/app', { replace: true });
  }, [onLogin, navigate]);

  useEffect(() => {
    if (searchParams.get('guest') !== '1' || guestAutoStarted.current) return;
    guestAutoStarted.current = true;
    handleGuestLogin();
  }, [searchParams, handleGuestLogin]);

  /**
   * Yalnızca Supabase URL/key yoksa: tek klinik için .env girişi (geliştirme / eski kurulum).
   * Supabase yapılandırılmışken ASLA çağrılmaz — aksi halde zayıf .env şifresi ikinci bir kapı olur.
   */
  const tryClinicEnvLogin = (): ClinicEnvResult => {
    if (process.env.NODE_ENV === 'production') {
      return { ok: false, reason: 'missing_env' };
    }
    const configuredEmail = process.env.REACT_APP_CLINIC_LOGIN_EMAIL?.trim().toLowerCase();
    const configuredPassword = process.env.REACT_APP_CLINIC_LOGIN_PASSWORD;
    const displayName =
      process.env.REACT_APP_CLINIC_DISPLAY_NAME?.trim() || 'Veteriner Hekim';

    if (!configuredEmail || !configuredPassword) {
      return { ok: false, reason: 'missing_env' };
    }

    if (BLOCKED_ENV_PASSWORDS.has(String(configuredPassword))) {
      console.error(
        '[Güvenlik] REACT_APP_CLINIC_LOGIN_PASSWORD çok zayıf. Netlify ortam değişkeninde güçlü bir şifre kullanın.'
      );
      return { ok: false, reason: 'weak_password' };
    }

    const inputEmail = formData.email.trim().toLowerCase();
    if (inputEmail === configuredEmail && formData.password === configuredPassword) {
      localStorage.setItem('authToken', 'clinic-token-' + Date.now());
      onLogin({
        name: displayName,
        email: formData.email.trim(),
        role: 'Veteriner Hekim',
        clinicId: process.env.REACT_APP_CLINIC_ID?.trim() || null,
        clinicName: process.env.REACT_APP_CLINIC_DISPLAY_NAME?.trim() || null,
        authProvider: 'env',
      });
      toast.success('Başarıyla giriş yapıldı!');
      navigate('/app');
      return { ok: true };
    }
    return { ok: false, reason: 'mismatch' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isSupabaseAuthConfigured()) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email.trim(),
          password: formData.password,
        });

        if (!error && data.user && data.session) {
          const profile = await loadUserProfile(data.user.id);
          if (!profile) {
            await supabase.auth.signOut();
            if (isDev) {
              console.warn(
                '[KlinikVet] profiles satırı yok: Supabase’te bu kullanıcı için id + clinic_id ile profiles ekleyin.'
              );
            }
            toast.error(
              isDev
                ? 'Hesabınız bir kliniğe atanmamış (profiles). Supabase’te clinic_id tanımlayın.'
                : 'Hesabınız henüz etkinleştirilmemiş. Klinik yöneticinizle görüşün.',
              { id: loginToastId }
            );
            return;
          }
          localStorage.setItem('authToken', data.session.access_token);
          onLogin({
            name:
              profile.displayName ||
              data.user.user_metadata?.full_name ||
              data.user.email ||
              '',
            email: data.user.email || '',
            role: 'Veteriner Hekim',
            clinicId: profile.clinicId,
            clinicName: profile.clinicName || null,
            authProvider: 'supabase',
          });
          toast.success('Başarıyla giriş yapıldı!');
          navigate('/app');
          return;
        }

        if (error) {
          console.warn('Supabase giriş başarısız:', error.message);
        }
        toast.error('E-posta veya şifre hatalı.', { id: loginToastId });
        return;
      }

      const envTry = tryClinicEnvLogin();
      if (envTry.ok) {
        return;
      }
      if (envTry.reason === 'weak_password') {
        if (isDev) {
          console.error(
            '[KlinikVet] REACT_APP_CLINIC_LOGIN_PASSWORD çok zayıf; ortam değişkeninde güçlü şifre kullanın.'
          );
        }
        toast.error(
          isDev
            ? 'Ortam şifresi (CLINIC_LOGIN) çok zayıf. .env / hosting’de güçlü bir değer kullanın.'
            : 'Giriş yapılandırması güvenlik gereği kullanılamıyor. Yöneticinizle iletişime geçin.',
          { id: loginToastId }
        );
        return;
      }
      if (envTry.reason === 'mismatch') {
        toast.error('E-posta veya şifre hatalı.', { id: loginToastId });
        return;
      }
      if (isDev) {
        console.info(
          '[KlinikVet] Yerel: .env içine REACT_APP_SUPABASE_URL ve REACT_APP_SUPABASE_ANON_KEY ekleyin (veya CLINIC_LOGIN_*). Örnek: .env.example'
        );
        toast.error(
          'Yerelde giriş ayarı yok. Proje kökünde .env oluşturup REACT_APP_SUPABASE_URL ve REACT_APP_SUPABASE_ANON_KEY tanımlayın; npm start’ı yeniden çalıştırın.',
          { id: loginToastId, duration: 9000 }
        );
      } else {
        toast.error(
          'Giriş şu an kullanılamıyor. Lütfen daha sonra tekrar deneyin veya klinik yönetimiyle iletişime geçin.',
          { id: loginToastId }
        );
      }
    } catch (error) {
      console.error('Login hatası:', error);
      toast.error('Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.', {
        id: loginToastId,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-white rounded-3xl border border-gray-200 shadow-xl shadow-teal-100/50 p-8 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-300/40">
              <PawPrint className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900">KlinikVet</h1>
          </div>
          <p className="text-gray-500 text-sm">Veteriner Kliniği Yönetim Sistemi</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="login-email" className="block text-sm font-semibold text-gray-700 mb-1.5">
              <User className="w-4 h-4 inline mr-1.5 text-gray-400" />
              E-posta
            </label>
            <input
              id="login-email"
              type="email"
              name="email"
              autoComplete="username"
              value={formData.email}
              onChange={handleChange}
              required
              className="input"
              placeholder="E-posta adresiniz"
            />
          </div>

          <div>
            <label htmlFor="login-password" className="block text-sm font-semibold text-gray-700 mb-1.5">
              <Lock className="w-4 h-4 inline mr-1.5 text-gray-400" />
              Şifre
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                required
                className="input pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-base shadow-lg shadow-teal-300/40 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Giriş yapılıyor...</span>
              </div>
            ) : (
              'Giriş Yap'
            )}
          </button>

          <div>
            <div className="my-4 flex items-center gap-3">
              <span className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-400">veya</span>
              <span className="h-px flex-1 bg-gray-200" />
            </div>
            <button
              type="button"
              onClick={handleGuestLogin}
              className="w-full py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 text-sm font-medium hover:bg-teal-50 hover:border-teal-200 hover:text-teal-700 transition-all inline-flex items-center justify-center gap-2"
            >
              <Scan className="w-4 h-4" />
              Misafir girişi (panel önizleme)
            </button>
            <p className="mt-2 text-center text-xs text-gray-400">
              Şifre gerekmez; veriler örnek veya yerel olabilir.
            </p>
          </div>
        </form>

        <div className="mt-6 pt-5 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">© 2026 KlinikVet · Tüm hakları saklıdır.</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;